import { useState, useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { getRutas, updateRutaPrice, searchUsuariosMoviles, createRuta, downloadPlantillaBulk, uploadRutasBulk, downloadPlantillaEdicionBulk, updateRutasBulk } from '@shared/helpers/api/mantenedores/rutas'
import { runGeosortDiario, runGeosortProcessBulk } from '@shared/helpers/api/mantenedores/geosort'
import { getEmpresas } from '@shared/helpers/api/mantenedores/empresas'
import { getTMS } from '@shared/helpers/api/mantenedores/generales'

/**
 * Hook para manejar el mantenedor de rutas con paginación, filtros y empresas
 */
export const useRutasMantenedor = (user, permisos) => {
    const [rutas, setRutas] = useState([])
    const [total, setTotal] = useState(0)
    const [loading, setLoading] = useState(true) // Inicializar en true para evitar mostrar "No se encontraron rutas"
    const [loadingInitial, setLoadingInitial] = useState(false) // Para la carga inicial de empresas/TMS
    const [error, setError] = useState(null)
    const [pageSize, setPageSize] = useState(10)
    const [pageNumber, setPageNumber] = useState(1)
    const [searchValue, setSearchValue] = useState('')
    const [empresaId, setEmpresaId] = useState('')
    const [empresas, setEmpresas] = useState([])
    const [tmsId, setTmsId] = useState('')
    const [tmsList, setTmsList] = useState([])
    const [filters, setFilters] = useState({})
    const [initialDataLoaded, setInitialDataLoaded] = useState(false) // Nueva bandera
    const [refreshTrigger, setRefreshTrigger] = useState(0) // Fuerza refetch aunque no cambien los demás filtros/página

    // Cargar empresas y TMS para los filtros
    useEffect(() => {
        const loadInitialData = async () => {
            setLoadingInitial(true)
            try {
                const [empresasRes, tmsRes] = await Promise.all([
                    getEmpresas(user),
                    getTMS(user)
                ])
                setEmpresas(empresasRes.success ? empresasRes.data : [])
                setTmsList(tmsRes.success ? tmsRes.data : [])
                setInitialDataLoaded(true) // Marcar que los datos iniciales están listos
            } catch (error) {
                console.error('Error al cargar datos iniciales:', error)
                setEmpresas([])
                setTmsList([])
                setInitialDataLoaded(true) // Incluso si hay error, permitir continuar
            } finally {
                setLoadingInitial(false)
            }
        }
        
        if (user) {
            loadInitialData()
        }
    }, [user])

    // Control de cancelación de requests
    const abortControllerRef = useRef();
    const lastFetchId = useRef(0);

    useEffect(() => {
        // Solo ejecutar si los datos iniciales ya están cargados
        if (!initialDataLoaded) {
            return;
        }

        // Solo buscar si searchValue tiene 2+ caracteres o está vacío
        if (searchValue.length > 0 && searchValue.length < 2) {
            setRutas([]);
            setTotal(0);
            setLoading(false);
            return;
        }

        // Cancelar request anterior
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        const controller = new AbortController();
        abortControllerRef.current = controller;
        const fetchId = ++lastFetchId.current;

        setLoading(true);
        setError(null);

        const fetchData = async () => {
            try {
                const res = await getRutas(
                    user,
                    {
                        ...filters,
                        empresa_id: empresaId || undefined,
                        tms_id: tmsId || undefined,
                        pageSize,
                        pageNumber,
                        searchValue: searchValue.length >= 2 ? searchValue : ''
                    }
                );
                // Solo actualizar si es la última request
                if (lastFetchId.current === fetchId && !controller.signal.aborted) {
                    if (res.success) {
                        setRutas(res.data);
                        setTotal(res.total);
                    } else {
                        setRutas([]);
                        setTotal(0);
                        setError(res.error);
                    }
                }
            } catch (err) {
                if (lastFetchId.current === fetchId && !controller.signal.aborted) {
                    setRutas([]);
                    setTotal(0);
                    setError('Error al cargar rutas');
                }
            }
            if (lastFetchId.current === fetchId && !controller.signal.aborted) {
                setLoading(false);
            }
        };
        fetchData();

        // Cleanup: cancelar si cambia dependencia
        return () => {
            controller.abort();
        };
    }, [user, pageSize, pageNumber, searchValue, empresaId, tmsId, filters, initialDataLoaded, refreshTrigger]); // refreshTrigger fuerza el refetch aunque pageNumber ya sea 1

    // Para live search
    const handleSearch = value => {
        setSearchValue(value)
        setPageNumber(1)
    }

    // Para cambio de empresa
    const handleEmpresaChange = id => {
        setEmpresaId(id)
        setPageNumber(1)
    }

    // Para cambio de TMS/Origen
    const handleTmsChange = id => {
        setTmsId(id)
        setPageNumber(1)
    }

    // Para paginación
    const handlePageChange = page => {
        setPageNumber(page)
    }
    const handlePageSizeChange = size => {
        setPageSize(size)
        setPageNumber(1)
    }

    // Para actualizar precio de ruta
    const handleUpdateRutaPrice = async (rutaId, updateData) => {
        try {
            const response = await updateRutaPrice(user, rutaId, updateData);
            return response;
        } catch (error) {
            console.error('Error al actualizar precio de ruta:', error);
            return {
                success: false,
                error: 'Error al actualizar precio de ruta'
            };
        }
    }

    // Función para refrescar la tabla
    const refreshRutas = () => {
        setPageNumber(1)
        // Si ya estábamos en la página 1, setPageNumber(1) no dispara el useEffect
        // (React no vuelve a renderizar si el valor no cambia), por eso se fuerza
        // el refetch incondicionalmente con refreshTrigger
        setRefreshTrigger(prev => prev + 1)
    }

    // Función para buscar usuarios móviles
    const searchUsuarios = async (searchValue) => {
        try {
            const response = await searchUsuariosMoviles(user, searchValue);
            return response;
        } catch (error) {
            console.error('Error al buscar usuarios móviles:', error);
            return {
                success: false,
                error: 'Error al buscar usuarios móviles',
                data: [],
                total: 0
            };
        }
    };

    // Función para crear nueva ruta
    const handleCreateRuta = async (rutaData) => {
        try {
            const response = await createRuta(user, rutaData);
            if (response.success) {
                // Refrescar la tabla después de crear
                refreshRutas();
            }
            return response;
        } catch (error) {
            console.error('Error al crear ruta:', error);
            return {
                success: false,
                error: 'Error al crear ruta'
            };
        }
    };

    // Función para descargar plantilla de carga masiva
    const handleDownloadPlantilla = async () => {
        try {
            const response = await downloadPlantillaBulk(user);
            return response;
        } catch (error) {
            console.error('Error al descargar plantilla:', error);
            return {
                success: false,
                error: 'Error al descargar plantilla'
            };
        }
    };

    // Función para subir archivo de carga masiva
    const handleUploadRutasBulk = async (file) => {
        try {
            const response = await uploadRutasBulk(user, file);
            if (response.success) {
                // Refrescar la tabla después de iniciar el proceso
                refreshRutas();
            }
            return response;
        } catch (error) {
            console.error('Error al subir archivo bulk:', error);
            return {
                success: false,
                error: 'Error al procesar el archivo'
            };
        }
    };

    // Función para descargar plantilla de edición masiva
    const handleDownloadPlantillaEdicion = async () => {
        try {
            const response = await downloadPlantillaEdicionBulk(user);
            return response;
        } catch (error) {
            console.error('Error al descargar plantilla de edición:', error);
            return {
                success: false,
                error: 'Error al descargar plantilla de edición'
            };
        }
    };

    // Función para subir archivo de edición masiva (ASÍNCRONO, fire-and-forget)
    // Igual que handleUploadRutasBulk (creación): el POST solo valida e inicia
    // el proceso en background, devuelve el process_id de inmediato y no se
    // hace ningún polling desde el navegador. El resultado final se notifica
    // por correo al usuario cuando el proceso termina.
    const handleUpdateRutasBulk = async (file) => {
        try {
            const response = await updateRutasBulk(user, file);
            if (response.success) {
                // Refrescar la tabla después de iniciar el proceso
                refreshRutas();
            }
            return response;
        } catch (error) {
            console.error('Error al editar rutas de forma masiva:', error);
            return {
                success: false,
                error: 'Error al procesar el archivo'
            };
        }
    };

    // Función para recargar (reprocesar) una fecha de GeoSort completa.
    // Dos llamadas SECUENCIALES a dos backends distintos:
    //   1) af_funciones_diarias (api/geosort_diario): vuelve a descargar el reporte
    //      diario de GeoSort desde la plataforma de Falabella y lo sube a Blob Storage.
    //      Síncrona: si falla (CSV no disponible, fecha inválida, etc.) NO se avanza
    //      al paso 2, para no reprocesar un CSV inexistente o viejo.
    //   2) myspread-flask (/geoSort/process-bulk-optimized): dispara el bulk insert,
    //      que corre en un thread en background — responde de inmediato con un
    //      total_guides estimado, el guardado real en BD sigue unos minutos más.
    // No se hace polling de ninguno de los dos pasos (mismo patrón fire-and-forget
    // que la carga masiva de rutas) — el resultado final del paso 2 no es visible
    // sincrónicamente, por eso solo se informa un estimado al usuario.
    const handleReloadGeosort = async (fecha) => {
        try {
            const descarga = await runGeosortDiario(user, fecha)
            if (!descarga.success) {
                return {
                    success: false,
                    step: 'descarga',
                    error: descarga.error || 'No se pudo descargar el reporte diario de GeoSort'
                }
            }

            const proceso = await runGeosortProcessBulk(user, fecha)
            if (!proceso.success) {
                return {
                    success: false,
                    step: 'proceso',
                    error: proceso.error || 'No se pudo iniciar el procesamiento de GeoSort'
                }
            }

            // Refrescar la tabla: el proceso sigue en background, pero es consistente
            // con el resto de los flujos asíncronos de este mantenedor (carga/edición masiva)
            refreshRutas()

            return {
                success: true,
                fecha: proceso.fecha || fecha,
                totalGuides: proceso.totalGuides,
                message: proceso.message
            }
        } catch (error) {
            console.error('Error al recargar fecha de GeoSort:', error)
            return {
                success: false,
                step: 'inesperado',
                error: 'Error inesperado al recargar la fecha de GeoSort'
            }
        }
    }

    return {
        rutas,
        total,
        loading, // Loading específico para rutas
        loadingInitial, // Loading para empresas/TMS
        error,
        pageSize,
        pageNumber,
        searchValue,
        empresaId,
        empresas,
        tmsId,
        tmsList,
        setFilters,
        handleSearch,
        handleEmpresaChange,
        handleTmsChange,
        handlePageChange,
        handlePageSizeChange,
        handleUpdateRutaPrice,
        refreshRutas,
        searchUsuarios,
        handleCreateRuta,
        handleDownloadPlantilla,
        handleUploadRutasBulk,
        handleDownloadPlantillaEdicion,
        handleUpdateRutasBulk,
        handleReloadGeosort
    }
}
